/*
  <lanelux-video-gallery> — a grid of muted looping video previews that open
  the clicked video in a shared full-screen modal.

  Registered as a custom element so it survives Shopify section re-renders in
  the theme editor without needing a section:load listener.
*/
if (!customElements.get('lanelux-video-gallery')) {
  customElements.define(
    'lanelux-video-gallery',
    class LaneLuxVideoGallery extends HTMLElement {
      connectedCallback() {
        this.modal = this.querySelector('[data-lanelux-video-modal]');
        this.player = this.querySelector('[data-lanelux-video-player]');

        if (!this.modal || !this.player) return;

        this.onKeydown = this.onKeydown.bind(this);
        this.close = this.close.bind(this);

        this.querySelectorAll('[data-lanelux-video-trigger]').forEach((trigger) => {
          trigger.addEventListener('click', () => this.open(trigger.dataset.videoSrc));
        });

        this.modal.querySelectorAll('[data-lanelux-video-close]').forEach((button) => {
          button.addEventListener('click', this.close);
        });

        /*
          The gallery sits in a sticky, z-indexed column, which is a stacking
          context — a fixed overlay inside it cannot paint above the buy box or
          the sticky add-to-cart bar no matter how high its z-index. Moving the
          modal to <body> takes it out of that context. Listeners are bound to
          the elements themselves, so they survive the move.
        */
        document.body.appendChild(this.modal);
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('lanelux-video-modal-open');
        // The modal no longer lives inside this element, so take it along.
        if (this.modal) this.modal.remove();
      }

      open(src) {
        if (!src) return;

        this.player.src = src;
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('lanelux-video-modal-open');
        document.addEventListener('keydown', this.onKeydown);

        const playback = this.player.play();
        if (playback && typeof playback.catch === 'function') {
          // Autoplay with sound can be blocked; the controls stay available.
          playback.catch(() => {});
        }

        this.modal.querySelector('[data-lanelux-video-close]')?.focus();
      }

      close() {
        this.player.pause();
        this.player.removeAttribute('src');
        this.player.load();
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('lanelux-video-modal-open');
        document.removeEventListener('keydown', this.onKeydown);
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }
    }
  );
}
